import { useCallback, useEffect, useMemo, useState } from "react";
import { MagnifyingGlass, UserPlus, Chalkboard, User, X, Plus, ShieldStar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import {
  findProfileByEmail,
  grantRole,
  listClassroomsForSelect,
  listStaff,
  revokeRole,
  ROLE_LABELS,
  ROLE_TONES,
} from "@/lib/admin/staff";
import {
  assignStaffToClassroom,
  deleteStaffAssignment,
  endStaffAssignment,
  STAFF_POSITIONS,
} from "@/lib/admin/classrooms";
import { adminInviteStaff } from "@/lib/admin/inviteStaff";

export default function AdminStaff() {
  const { profile } = useAuth();
  const organizationId = profile?.organization_id;
  const [rows, setRows] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drawer, setDrawer] = useState(null); // { kind: 'invite'|'detail', profileId?, roleRows?, assignments? }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, rooms] = await Promise.all([
        listStaff({ organizationId }),
        listClassroomsForSelect(),
      ]);
      setRows(list);
      setClassrooms(rooms);
    } catch (err) {
      toast.error(err.message || "Could not load staff");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { if (organizationId) load(); }, [organizationId, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.profile.full_name || "").toLowerCase().includes(q) ||
        (r.profile.email || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const kpis = {
    total: rows.length,
    teachers: rows.filter((r) => r.roles.includes("teacher")).length,
    admins: rows.filter((r) => r.roles.includes("admin")).length,
    unassigned: rows.filter((r) => r.roles.includes("teacher") && r.active_assignment_count === 0).length,
  };

  return (
    <div data-testid="admin-staff" className="space-y-6">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Operational
        </p>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Staff
            </h1>
            <p className="mt-2 max-w-2xl text-neutral-600">
              Add a teacher or admin by email. If they already have an
              account we'll attach it to your center; if they don't, we'll
              send them an onboarding invite. Optionally assign them to a
              classroom in the same step.
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-staff-new"
            onClick={() => setDrawer({ kind: "invite" })}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
          >
            <UserPlus size={14} weight="bold" />
            Add staff
          </button>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi testId="admin-staff-kpi-total" label="Total" value={String(kpis.total)} />
        <Kpi testId="admin-staff-kpi-teachers" label="Teachers" value={String(kpis.teachers)} accent />
        <Kpi testId="admin-staff-kpi-admins" label="Admins" value={String(kpis.admins)} />
        <Kpi testId="admin-staff-kpi-unassigned" label="Unassigned teachers" value={String(kpis.unassigned)} />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <MagnifyingGlass size={18} weight="duotone" className="text-primary-hover" />
          <input
            data-testid="admin-staff-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            className="flex-1 rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </section>

      {loading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyCard testId="admin-staff-empty" title="No staff yet" desc="Add teachers and admins by email to get started." />
      ) : (
        <ul data-testid="admin-staff-list" className="space-y-2">
          {filtered.map((r) => (
            <li
              key={r.profile.id}
              data-testid={`admin-staff-row-${r.profile.id}`}
              className="rounded-2xl border border-neutral-200 bg-white p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-secondary/30">
                    <User size={18} weight="duotone" className="text-primary-hover" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-heading text-base font-semibold text-neutral-900">
                      {r.profile.full_name || r.profile.email}
                    </p>
                    <p className="truncate text-xs text-neutral-500">
                      {r.profile.email}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {r.roles.map((role) => (
                        <span
                          key={role}
                          data-testid={`admin-staff-row-${r.profile.id}-role-${role}`}
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ROLE_TONES[role]}`}
                        >
                          {ROLE_LABELS[role] || role}
                        </span>
                      ))}
                      {r.roles.includes("teacher") && (
                        <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-600">
                          {r.active_assignment_count} classroom{r.active_assignment_count === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={`admin-staff-row-${r.profile.id}-manage`}
                  onClick={() =>
                    setDrawer({
                      kind: "detail",
                      profile: r.profile,
                      roleRows: r.role_rows,
                      assignments: r.assignments,
                    })
                  }
                  className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/10"
                >
                  <ShieldStar size={11} weight="duotone" />
                  Manage
                </button>
              </div>

              {r.active_assignments.length > 0 && (
                <ul className="mt-3 flex flex-wrap gap-1.5">
                  {r.active_assignments.map((a) => (
                    <li
                      key={a.id}
                      className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[10px] font-semibold text-neutral-700"
                    >
                      <Chalkboard size={10} weight="duotone" />
                      {a.classroom?.name} · {a.position}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {drawer?.kind === "invite" && (
        <InviteSheet
          organizationId={profile?.organization_id}
          classrooms={classrooms}
          onClose={() => setDrawer(null)}
          onSaved={() => { /* keep sheet open to show success card */ load(); }}
        />
      )}
      {drawer?.kind === "detail" && (
        <StaffDetailSheet
          profile={drawer.profile}
          roleRows={drawer.roleRows}
          assignments={drawer.assignments}
          organizationId={profile?.organization_id}
          classrooms={classrooms}
          onClose={() => setDrawer(null)}
          onChanged={() => { setDrawer(null); load(); }}
        />
      )}
    </div>
  );
}

function InviteSheet({ organizationId, classrooms, onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("teacher");
  const [classroomId, setClassroomId] = useState("");
  const [position, setPosition] = useState("lead_teacher");
  const [busy, setBusy] = useState(false);
  // Stepper state for the success card after the invite completes.
  // shape: { profile_id, email, full_name, already_existed, invite_email_sent,
  //          branded_email_sent, needs_password_setup, role_granted,
  //          classroom_assigned }
  const [result, setResult] = useState(null);

  const onSubmit = async () => {
    const e = email.trim().toLowerCase();
    if (!e) return toast.error("Email required");
    setBusy(true);

    let stepResult = {
      profile_id: null,
      email: e,
      full_name: fullName.trim() || null,
      already_existed: false,
      invite_email_sent: false,
      branded_email_sent: false,
      needs_password_setup: false,
      role_granted: false,
      classroom_assigned: false,
    };

    try {
      // Step 1 — invite-or-attach via the FastAPI proxy. Try the proxy
      // first; if it 503s (service role not configured) we fall back to
      // the legacy "must already exist" path so the page still works in
      // dev environments without service-role secrets.
      let inviteOk = false;
      try {
        const apiResp = await adminInviteStaff({
          email: e,
          fullName: fullName.trim() || null,
          organizationId,
          role,
          redirectTo: window.location.origin + "/login",
        });
        stepResult = { ...stepResult, ...apiResp };
        inviteOk = true;
      } catch (apiErr) {
        // Service-role-not-configured fallback: try the legacy attach-
        // existing-user path so devs without the service key can still
        // grant roles. Branches on the typed `code` from inviteStaff.js
        // (set when the proxy returns 503) — string regex was fragile.
        const looksLikeMissingService =
          apiErr.code === "SERVICE_ROLE_MISSING" ||
          apiErr.status === 503 ||
          /service.?role|configuration|503/i.test(String(apiErr.message || ""));
        if (!looksLikeMissingService) {
          throw apiErr;
        }
        const found = await findProfileByEmail(e);
        if (!found) {
          toast.error(
            apiErr.message ||
              "Staff invites aren't configured yet — ask your dev to set SUPABASE_SERVICE_ROLE_KEY, or have the teacher sign up first.",
          );
          return;
        }
        stepResult.profile_id = found.id;
        stepResult.full_name = found.full_name || stepResult.full_name;
        stepResult.already_existed = true;
        inviteOk = true;
      }
      if (!inviteOk || !stepResult.profile_id) return;

      // Step 2 — grant the chosen role. RLS handles cross-org safety;
      // duplicates are ignored gracefully.
      try {
        await grantRole({
          organizationId,
          profileId: stepResult.profile_id,
          role,
        });
        stepResult.role_granted = true;
      } catch (err) {
        if (
          err.code === "23505" ||
          String(err.message || "").includes("duplicate")
        ) {
          // Already had the role — that's fine.
          stepResult.role_granted = true;
        } else {
          throw err;
        }
      }

      // Step 3 — optional classroom assignment (teacher role only).
      if (role === "teacher" && classroomId) {
        try {
          await assignStaffToClassroom({
            organizationId,
            profileId: stepResult.profile_id,
            classroomId,
            position,
          });
          stepResult.classroom_assigned = true;
        } catch (err) {
          // Non-fatal: surface a soft warning but keep the invite as a win.
          // eslint-disable-next-line no-console
          console.warn("[invite] classroom assign failed:", err.message);
        }
      }

      setResult(stepResult);
      onSaved();
    } catch (err) {
      toast.error(err.message || "Could not invite staff");
    } finally {
      setBusy(false);
    }
  };

  const input =
    "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-md"
        data-testid="admin-staff-invite-sheet"
      >
        <SheetHeader>
          <SheetTitle>Add staff</SheetTitle>
          <SheetDescription>
            We'll attach an existing account or send an onboarding invite —
            whichever applies.
          </SheetDescription>
        </SheetHeader>

        {result ? (
          <div className="mt-5 space-y-3" data-testid="admin-staff-invite-result">
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
              <p className="font-heading text-base font-semibold text-neutral-900">
                {result.already_existed
                  ? `${result.full_name || result.email} attached`
                  : `Invite sent to ${result.email}`}
              </p>
              <ul className="mt-3 space-y-1.5 text-sm text-neutral-700">
                <li>
                  <CheckMark on={result.already_existed} />{" "}
                  Existing account found and attached
                </li>
                <li>
                  <CheckMark on={result.invite_email_sent} />{" "}
                  Onboarding email sent (Supabase magic link)
                </li>
                <li>
                  <CheckMark on={result.branded_email_sent} />{" "}
                  Branded follow-up email (Resend){" "}
                  {!result.branded_email_sent && (
                    <span className="text-xs text-neutral-500">
                      — optional, set RESEND_API_KEY to enable
                    </span>
                  )}
                </li>
                <li>
                  <CheckMark on={result.role_granted} />{" "}
                  {ROLE_LABELS[role]} role granted
                </li>
                <li>
                  <CheckMark
                    on={result.classroom_assigned}
                    skip={!classroomId || role !== "teacher"}
                  />{" "}
                  {classroomId && role === "teacher"
                    ? "Assigned to classroom"
                    : "Classroom assignment (skipped)"}
                </li>
              </ul>
              {result.needs_password_setup && (
                <p className="mt-3 rounded-xl bg-white/60 p-2.5 text-xs leading-relaxed text-neutral-600">
                  They'll click the email link to set their password and
                  finish onboarding. The link expires in 24 hours.
                </p>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                data-testid="admin-staff-invite-close"
                onClick={onClose}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                Email
              </span>
              <input
                data-testid="admin-staff-invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teacher@daycare.test"
                className={input}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                Full name <span className="font-normal text-neutral-400">(optional)</span>
              </span>
              <input
                data-testid="admin-staff-invite-name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jordan Smith"
                className={input}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                Role
              </span>
              <select
                data-testid="admin-staff-invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className={input}
              >
                <option value="teacher">Teacher</option>
                <option value="admin">Admin</option>
                <option value="cms_editor">CMS editor</option>
              </select>
            </label>

            {role === "teacher" && (
              <div className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-3 space-y-2.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                  Classroom (optional)
                </span>
                <label className="block">
                  <select
                    data-testid="admin-staff-invite-classroom"
                    value={classroomId}
                    onChange={(e) => setClassroomId(e.target.value)}
                    className={input}
                  >
                    <option value="">— Don't assign now —</option>
                    {(classrooms || []).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </label>
                {classroomId && (
                  <label className="block">
                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                      Position
                    </span>
                    <select
                      data-testid="admin-staff-invite-position"
                      value={position}
                      onChange={(e) => setPosition(e.target.value)}
                      className={input}
                    >
                      {STAFF_POSITIONS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                data-testid="admin-staff-invite-submit"
                disabled={busy || !email.trim()}
                onClick={onSubmit}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
              >
                {busy ? "Working…" : "Send invite"}
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CheckMark({ on, skip = false }) {
  if (skip) {
    return <span aria-hidden className="text-neutral-400">○</span>;
  }
  return on ? (
    <span aria-hidden className="text-primary-hover">✓</span>
  ) : (
    <span aria-hidden className="text-neutral-400">○</span>
  );
}

function StaffDetailSheet({ profile, roleRows, assignments, organizationId, classrooms, onClose, onChanged }) {
  const today = new Date().toISOString().slice(0, 10);
  const active = (assignments || []).filter((a) => !a.ends_on || a.ends_on >= today);
  const past = (assignments || []).filter((a) => a.ends_on && a.ends_on < today);

  const [newClassroomId, setNewClassroomId] = useState("");
  const [newPosition, setNewPosition] = useState("lead_teacher");
  const [adding, setAdding] = useState(false);

  const onAssign = async () => {
    if (!newClassroomId) return toast.error("Pick a classroom");
    setAdding(true);
    try {
      await assignStaffToClassroom({
        organizationId,
        profileId: profile.id,
        classroomId: newClassroomId,
        position: newPosition,
      });
      toast.success("Assigned");
      onChanged();
    } catch (err) {
      toast.error(err.message || "Could not assign");
    } finally {
      setAdding(false);
    }
  };

  const onEnd = async (assignmentId) => {
    const date = window.prompt("End date (YYYY-MM-DD):", today);
    if (!date) return;
    try {
      await endStaffAssignment(assignmentId, date);
      toast.success("Assignment ended");
      onChanged();
    } catch (err) {
      toast.error(err.message || "Could not end");
    }
  };

  const onRemoveAssignment = async (assignmentId) => {
    if (!window.confirm("Remove this assignment history entry?")) return;
    try {
      await deleteStaffAssignment(assignmentId);
      toast.success("Removed");
      onChanged();
    } catch (err) {
      toast.error(err.message || "Could not remove");
    }
  };

  const onRevokeRole = async (row) => {
    if (!window.confirm(`Revoke the ${ROLE_LABELS[row.role]} role from ${profile.full_name || profile.email}?`)) return;
    try {
      await revokeRole(row.id);
      toast.success("Role revoked");
      onChanged();
    } catch (err) {
      toast.error(err.message || "Could not revoke");
    }
  };

  const input = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid="admin-staff-detail-sheet">
        <SheetHeader>
          <SheetTitle>{profile.full_name || profile.email}</SheetTitle>
          <SheetDescription>{profile.email}</SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          <section>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">Roles</p>
            <ul data-testid="admin-staff-detail-roles" className="space-y-1.5">
              {roleRows.map((row) => (
                <li
                  key={row.id}
                  data-testid={`admin-staff-detail-role-${row.role}`}
                  className="flex items-center justify-between rounded-xl border border-neutral-200 p-3"
                >
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ROLE_TONES[row.role]}`}>
                    {ROLE_LABELS[row.role] || row.role}
                  </span>
                  <button
                    type="button"
                    data-testid={`admin-staff-detail-role-${row.role}-revoke`}
                    onClick={() => onRevokeRole(row)}
                    className="rounded-full p-1.5 text-neutral-400 hover:bg-[#C6851E]/10 hover:text-[#C6851E]"
                    aria-label="Revoke role"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Active classroom assignments ({active.length})
            </p>
            <ul data-testid="admin-staff-detail-active" className="space-y-1.5">
              {active.length === 0 ? (
                <li className="rounded-xl border border-dashed border-neutral-200 p-3 text-xs text-neutral-500">
                  Not assigned to any classroom.
                </li>
              ) : (
                active.map((a) => (
                  <li key={a.id} data-testid={`admin-staff-detail-assignment-${a.id}`} className="flex items-center justify-between rounded-xl border border-neutral-200 p-3">
                    <div>
                      <p className="text-sm font-semibold text-neutral-900">
                        {a.classroom?.name || "—"}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {STAFF_POSITIONS.find((p) => p.value === a.position)?.label || a.position} · Started {a.starts_on}
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid={`admin-staff-detail-assignment-${a.id}-end`}
                      onClick={() => onEnd(a.id)}
                      className="rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-1 text-[10px] font-semibold text-[#C6851E] hover:bg-[#C6851E]/10"
                    >
                      End
                    </button>
                  </li>
                ))
              )}
            </ul>

            <div className="mt-3 space-y-2 rounded-xl border border-primary/30 bg-primary/5 p-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">
                Assign to classroom
              </p>
              <div className="flex gap-2">
                <select
                  data-testid="admin-staff-detail-new-classroom"
                  value={newClassroomId}
                  onChange={(e) => setNewClassroomId(e.target.value)}
                  className={`${input} flex-1`}
                >
                  <option value="">— classroom —</option>
                  {classrooms.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <select
                  data-testid="admin-staff-detail-new-position"
                  value={newPosition}
                  onChange={(e) => setNewPosition(e.target.value)}
                  className={input}
                >
                  {STAFF_POSITIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid="admin-staff-detail-new-submit"
                  disabled={adding || !newClassroomId}
                  onClick={onAssign}
                  className="rounded-full bg-primary px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
                >
                  {adding ? "…" : <Plus size={12} weight="bold" />}
                </button>
              </div>
            </div>
          </section>

          {past.length > 0 && (
            <section>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                Past assignments
              </p>
              <ul className="space-y-1.5" data-testid="admin-staff-detail-past">
                {past.map((a) => (
                  <li key={a.id} className="flex items-center justify-between rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600">
                    <span>
                      {a.classroom?.name || "—"} · {a.starts_on} → {a.ends_on}
                    </span>
                    <button
                      type="button"
                      data-testid={`admin-staff-detail-assignment-${a.id}-remove`}
                      onClick={() => onRemoveAssignment(a.id)}
                      className="rounded-full p-1 text-neutral-400 hover:bg-[#C6851E]/10 hover:text-[#C6851E]"
                      aria-label="Remove history entry"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-5 ${accent ? "border-primary/30" : "border-neutral-200"}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-semibold tracking-tight ${accent ? "text-primary-hover" : "text-neutral-900"}`}>{value}</p>
    </div>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-16"><div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" /></div>;
}

function EmptyCard({ title, desc, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center">
      <p className="font-heading text-base font-semibold text-neutral-900">{title}</p>
      <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">{desc}</p>
    </div>
  );
}
